// Client-safe types + validation schemas for the Events calendar.
import { z } from "zod";

export type EventRow = {
  id: string;
  created_at: string;
  slug: string;
  title: string;
  organizer: string | null;
  organizer_url: string | null;
  description: string;
  start_date: string;
  end_date: string | null;
  venue: string | null;
  city: string | null;
  country: string | null;
  event_type: string;
  online: boolean;
  website_url: string | null;
  image_url: string | null;
  source: string;
  status: "pending" | "approved" | "rejected";
  moderation_note: string | null;
  featured: boolean;
  sponsor_label: string | null;
  sponsor_url: string | null;
  sponsored_until: string | null;
};

/** A listing counts as sponsored while its paid window is still open. */
export function isSponsored(row: { sponsored_until?: string | null }): boolean {
  if (!row.sponsored_until) return false;
  return new Date(row.sponsored_until).getTime() > Date.now();
}

export function sponsorLabel(row: { sponsor_label?: string | null }): string {
  return (row.sponsor_label || "Sponsored").trim().slice(0, 40);
}

/** Keeps active sponsored listings pinned to the top without changing other ordering. */
export function sortSponsoredFirst<T extends { sponsored_until?: string | null }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => Number(isSponsored(b)) - Number(isSponsored(a)));
}



export const EMPLOYMENT_TYPES = [
  { value: "full_time", label: "Full-time" },
  { value: "part_time", label: "Part-time" },
  { value: "contract", label: "Contract" },
  { value: "internship", label: "Internship" },
  { value: "temporary", label: "Temporary" },
] as const;

export const SENIORITIES = ["Entry", "Mid", "Senior", "Lead", "Director", "C-level"] as const;

export const EVENT_TYPES = [
  { value: "conference", label: "Conference" },
  { value: "trade_show", label: "Trade show" },
  { value: "expo", label: "Expo" },
  { value: "summit", label: "Summit" },
  { value: "webinar", label: "Webinar" },
  { value: "awards", label: "Awards" },
] as const;

export function employmentLabel(value: string): string {
  return EMPLOYMENT_TYPES.find((t) => t.value === value)?.label ?? "Full-time";
}

export function eventTypeLabel(value: string): string {
  return EVENT_TYPES.find((t) => t.value === value)?.label ?? "Event";
}

const captcha = {
  captcha_answer: z.number().int(),
  captcha_a: z.number().int().min(1).max(9),
  captcha_b: z.number().int().min(1).max(9),
  honeypot: z.string().max(0).optional().or(z.literal("")),
  started_at: z.number().int(),
};

export const SubmitEventSchema = z.object({
  title: z.string().trim().min(4).max(160),
  organizer: z.string().trim().max(160).optional().or(z.literal("")),
  organizer_url: z.string().trim().url().max(400).optional().or(z.literal("")),
  description: z.string().trim().min(80).max(20000),
  start_date: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/),
  end_date: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal("")),
  venue: z.string().trim().max(200).optional().or(z.literal("")),
  city: z.string().trim().max(120).optional().or(z.literal("")),
  country: z.string().trim().max(120).optional().or(z.literal("")),
  event_type: z.enum(["conference", "trade_show", "expo", "summit", "webinar", "awards"]),
  online: z.boolean().optional(),
  website_url: z.string().trim().url().max(500).optional().or(z.literal("")),
  image_url: z.string().trim().url().max(500).optional().or(z.literal("")),
  contact_email: z.string().trim().email().max(254),
  ...captcha,
});

export type SubmitEventInput = z.infer<typeof SubmitEventSchema>;

export function formatEventDates(start: string, end?: string | null): string {
  const fmt = (d: string) =>
    new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  if (!end || end === start) return fmt(start);
  return `${fmt(start)}, ${fmt(end)}`;
}
